/**
 * <PdfPages> — les pages d'un PDF ouvert, dessinées par pdf.js, l'une sous l'autre (web).
 *
 * La largeur : la plus large page tient dans la place donnée (sans dépasser `MAX_PAGE_WIDTH`, la
 * largeur d'une feuille lue à l'écran), les autres suivent la même échelle ; `zoom` la multiplie, et
 * au-delà de la largeur le document défile aussi de côté. Les pages se posent dans ce qui défile
 * autour d'elles — le défilement de la visionneuse, ou la page qui porte l'énoncé.
 *
 * Une page ne se dessine qu'à l'approche de l'écran, et s'efface quand elle s'en éloigne (son canevas
 * et ce que pdf.js en garde) : un document de trente pages ne tient en mémoire que les quelques pages
 * autour de la lecture, ce qui compte sur un iPad, dont Safari ferme l'onglet au-delà de quelques
 * centaines de Mo de canevas.
 */
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { shadowStyle } from "../../utils/shadow"
import type { PdfDocument } from "./pdfjs"

/** Les dimensions d'une page à l'échelle 1 (points PDF). */
export type PdfPageSize = { width: number; height: number }

type PdfPagesProps = {
    doc: PdfDocument
    pages: PdfPageSize[]
    /** La place donnée, en points CSS. */
    boxWidth: number
    zoom?: number
    /** Un appui sur une page (dans la page : l'ouvrir en grand). Absent : rien. */
    onPressPage?: () => void
}

/** Une feuille A4 lue à l'écran, à peu près à 110 % : plus large, elle se lit moins bien. */
const MAX_PAGE_WIDTH = 900

/** Au plus 2 pixels par point CSS, et 5 M pixels par page : la netteté, sans saturer un iPad. */
const MAX_PIXEL_RATIO = 2
const MAX_CANVAS_PIXELS = 5_000_000

/** Une page commence à se dessiner un écran avant d'arriver, et s'efface un écran après être passée. */
const PRELOAD_MARGIN = "100% 0px"

const TEXTS = { open: "Ouvrir le PDF en grand" }

/** Le premier ancêtre qui défile : la marge d'approche se compte sur lui. */
function scrollParentOf(element: HTMLElement | null): HTMLElement | null {
    for (let node = element?.parentElement ?? null; node != null; node = node.parentElement) {
        if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node
    }
    return null
}

export function PdfPages(props: PdfPagesProps) {
    const { doc, pages, boxWidth, zoom = 1, onPressPage } = props
    const { theme } = useUnistyles()
    const [column, setColumn] = useState<HTMLDivElement | null>(null)
    const root = useMemo(() => scrollParentOf(column), [column])

    const widest = Math.max(...pages.map((page) => page.width))
    const scale = (Math.min(boxWidth, MAX_PAGE_WIDTH) / widest) * zoom

    return (
        <div
            ref={setColumn}
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: theme.spacing["3"],
                width: "max-content",
                minWidth: "100%",
            }}
        >
            {column != null &&
                boxWidth > 0 &&
                pages.map((page, index) => (
                    <PdfPage
                        key={index}
                        doc={doc}
                        index={index}
                        count={pages.length}
                        width={Math.floor(page.width * scale)}
                        height={Math.floor(page.height * scale)}
                        root={root}
                        onPress={onPressPage}
                    />
                ))}
        </div>
    )
}

type PdfPageProps = {
    doc: PdfDocument
    index: number
    count: number
    width: number
    height: number
    /** Ce qui défile autour de la page ; `null` : la fenêtre. */
    root: Element | null
    onPress?: () => void
}

function PdfPage(props: PdfPageProps) {
    const { doc, index, count, width, height, root, onPress } = props
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const drawnRef = useRef(false)
    const [near, setNear] = useState(false)

    useEffect(() => {
        const canvas = canvasRef.current
        if (canvas == null) return
        const observer = new IntersectionObserver(([entry]) => setNear(entry?.isIntersecting ?? false), {
            root,
            rootMargin: PRELOAD_MARGIN,
        })
        observer.observe(canvas)
        return () => observer.disconnect()
    }, [root])

    // Une page dessinée qui se démonte (la visionneuse se ferme, le document reste à l'énoncé) rend aussi
    // ce que pdf.js en garde.
    useEffect(
        () => () => {
            if (drawnRef.current) {
                void doc
                    .getPage(index + 1)
                    .then((page) => page.cleanup())
                    .catch(() => {})
            }
        },
        [doc, index]
    )

    useEffect(() => {
        const canvas = canvasRef.current
        if (canvas == null) return
        if (!near) {
            if (!drawnRef.current) return
            drawnRef.current = false
            // Un canevas vidé rend sa mémoire ; la page garde sa place, le fond de la feuille. pdf.js,
            // lui, garde la page lue (ses opérations, ses images décodées) tant qu'on ne la nettoie pas.
            canvas.width = 0
            canvas.height = 0
            void doc
                .getPage(index + 1)
                .then((page) => page.cleanup())
                .catch(() => {})
            return
        }
        let cancelled = false
        let task: { cancel: () => void } | null = null
        doc.getPage(index + 1)
            .then((page) => {
                const context = canvas.getContext("2d")
                if (cancelled || context == null) return
                const base = page.getViewport({ scale: 1 })
                const ratio = Math.min(
                    window.devicePixelRatio || 1,
                    MAX_PIXEL_RATIO,
                    Math.sqrt(MAX_CANVAS_PIXELS / Math.max(width * height, 1))
                )
                const viewport = page.getViewport({ scale: (width / base.width) * ratio })
                canvas.width = Math.floor(viewport.width)
                canvas.height = Math.floor(viewport.height)
                drawnRef.current = true
                const render = page.render({ canvasContext: context, viewport })
                task = render
                return render.promise
            })
            .catch(() => {
                // Un dessin annulé (la page s'éloigne, le zoom change) rejette : rien à dire. Une page
                // qui ne se dessine pas reste une feuille blanche ; les autres se lisent.
            })
        return () => {
            cancelled = true
            task?.cancel()
        }
    }, [near, doc, index, width, height])

    const sheet = (
        <View style={styles.page(width, height)}>
            {/* Vide tant qu'elle n'est pas dessinée (et non les 300 × 150 d'un canevas par défaut). */}
            <canvas
                ref={canvasRef}
                width={0}
                height={0}
                role="img"
                aria-label={`Page ${index + 1} sur ${count}`}
                style={{ display: "block", width: "100%", height: "100%" }}
            />
        </View>
    )
    if (onPress == null) return sheet
    return (
        <Pressable onPress={onPress} accessibilityRole="button" accessibilityLabel={TEXTS.open}>
            {sheet}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    page: (width: number, height: number) => ({
        width,
        height,
        backgroundColor: theme.colors.white,
        borderRadius: theme.radius.sm,
        overflow: "hidden" as const,
        ...shadowStyle(theme, "card"),
    }),
}))
